import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Tabs, Tab } from '@mui/material';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { useAppSelector } from 'store/hook';
import MaterialCategoryAPI from 'api/material-category.api';
import GradeCategoryAPI from 'api/grade-category.api';
import {
  type MaterialCategoryRecord,
  type GradeCategoryRecord,
} from './CategoryManagement/types';
import { MaterialCategoryTab } from './CategoryManagement/MaterialCategoryTab';
import { GradeCategoryTab } from './CategoryManagement/GradeCategoryTab';
import { GradesTab } from './GradesTab';
import { MaterialGradeTonnageTab } from './MaterialGradeTonnage/MaterialGradeTonnageTab';
import { type GeoJSONPolygon } from 'utils/geohash';

export interface GradeRecord {
  id: string;
  name: string;
  materialCategory: string | null;
  locationName: string | null;
  locationId: string | null;
  dump: string;
  blend: string;
  active: boolean;
  gradeCategory: string | null;
  specification: string;
  inventory: number | null;
  specificGravity: number | null;
  qualities: Record<string, number>;
  updatedAt?: string | null;
  zoneGeog?: GeoJSONPolygon | null;
}

const GradeImporterContent = (): JSX.Element => {
  const { t } = useTranslation();
  const { accessToken, customer } = useAppSelector((s) => s.authReducer);
  const customerId = customer.id;
  const materialCategoryApi = useRef(new MaterialCategoryAPI()).current;
  const gradeCategoryApi = useRef(new GradeCategoryAPI()).current;

  const [materialCategories, setMaterialCategories] = useState<
    MaterialCategoryRecord[]
  >([]);
  const [gradeCategories, setGradeCategories] = useState<GradeCategoryRecord[]>(
    []
  );
  const [activeTab, setActiveTab] = useState<
    'grades' | 'material' | 'gradeCategory' | 'truckPayload'
  >('grades');
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const loadingCountRef = useRef(0);

  const beginCategoriesLoading = useCallback(() => {
    loadingCountRef.current += 1;
    setCategoriesLoading(true);
  }, []);

  const endCategoriesLoading = useCallback(() => {
    loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
    if (loadingCountRef.current === 0) {
      setCategoriesLoading(false);
    }
  }, []);

  const fetchMaterialCategories = useCallback(async () => {
    if (!accessToken || !customerId) return;
    beginCategoriesLoading();
    try {
      setMaterialCategories(await materialCategoryApi.getAll());
    } catch (e) {
      console.error(e);
    } finally {
      endCategoriesLoading();
    }
  }, [
    accessToken,
    customerId,
    materialCategoryApi,
    beginCategoriesLoading,
    endCategoriesLoading,
  ]);

  const fetchGradeCategories = useCallback(async () => {
    if (!accessToken || !customerId) return;
    beginCategoriesLoading();
    try {
      setGradeCategories(await gradeCategoryApi.getAll());
    } catch (e) {
      console.error(e);
    } finally {
      endCategoriesLoading();
    }
  }, [
    accessToken,
    customerId,
    gradeCategoryApi,
    beginCategoriesLoading,
    endCategoriesLoading,
  ]);

  useEffect(() => {
    void fetchMaterialCategories();
    void fetchGradeCategories();
  }, [fetchMaterialCategories, fetchGradeCategories]);

  return (
    <Box
      sx={{
        px: '32px',
        pt: '20px',
        pb: '20px',
        mx: -4,
        height: 'calc(100vh - 130px)',
        minHeight: 500,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        variant="h5"
        fontWeight={700}
        color="text.primary"
        lineHeight={1.2}
      >
        {t('grade_importer_title')}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 0.4, mb: 2 }}
      >
        {t('grade_importer_subtitle')}
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_e, value) => {
          setActiveTab(value as typeof activeTab);
        }}
        sx={{
          mb: 2,
          minHeight: 36,
          '& .MuiTab-root': {
            minHeight: 36,
            textTransform: 'none',
            fontWeight: 600,
          },
        }}
      >
        <Tab label={t('grade_importer_tab_grades')} value="grades" />
        <Tab
          label={t('grade_importer_tab_material_category')}
          value="material"
        />
        <Tab
          label={t('grade_importer_tab_grade_category')}
          value="gradeCategory"
        />
        <Tab
          label={t('grade_importer_tab_truck_payload')}
          value="truckPayload"
        />
      </Tabs>

      <Box
        sx={{
          flex: 1,
          display: activeTab === 'grades' ? 'flex' : 'none',
          overflow: 'hidden',
        }}
      >
        <GradesTab
          materialCategories={materialCategories}
          gradeCategories={gradeCategories}
          onRefetchMaterialCategories={fetchMaterialCategories}
          onRefetchGradeCategories={fetchGradeCategories}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          display: activeTab === 'material' ? 'flex' : 'none',
          overflow: 'hidden',
        }}
      >
        <MaterialCategoryTab
          records={materialCategories}
          loading={categoriesLoading}
          onRefetch={fetchMaterialCategories}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          display: activeTab === 'gradeCategory' ? 'flex' : 'none',
          overflow: 'hidden',
        }}
      >
        <GradeCategoryTab
          records={gradeCategories}
          loading={categoriesLoading}
          onRefetch={fetchGradeCategories}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          display: activeTab === 'truckPayload' ? 'flex' : 'none',
          overflow: 'hidden',
        }}
      >
        <MaterialGradeTonnageTab
          materialCategories={materialCategories}
          onRefetchMaterialCategories={fetchMaterialCategories}
        />
      </Box>
    </Box>
  );
};

export const GradeImporterView = (): JSX.Element => {
  return (
    <CustomMuiThemeProvider>
      <GradeImporterContent />
    </CustomMuiThemeProvider>
  );
};
